import { useCallback } from 'react'

import { useClose } from '@headlessui/react'

import OpenNewTabIcon from '@/assets/icons/icon-open-new-tab.svg'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { useOpenChat } from '@/modules/chatting/hooks/use-open-chat'
import { useToggleFollow } from '@/modules/chatting/hooks/use-toggle-follow'
import ChatIcon from '@/modules/chatting/icons/chat.svg'
import NoteIcon from '@/modules/chatting/icons/description.svg'
import FollowIcon from '@/modules/chatting/icons/follow.svg'
import RestrictIcon from '@/modules/chatting/icons/restrict.svg'
import { useChattingTabs } from '@/modules/chatting/state/tabs-provider.ts'
import type { ListUser } from '@/modules/chatting/types'

import { useCollectionContext } from '../../../../CollectionProvider'

interface Props {
  user: ListUser
  onClickEditNotes: () => void
  onClickRestrict: () => void
  isRestrictActionLoading: boolean
}

export const SubscribedUserActions = ({
  user,
  onClickEditNotes,
  onClickRestrict,
  isRestrictActionLoading,
}: Props) => {
  const { handleOpenChat, isChatOpening } = useOpenChat()
  const closeCollectionsDialog = useClose()
  const { updateUserInCache } = useCollectionContext()
  const { openNewTab, isTabsLimitReached } = useChattingTabs()

  const onToggleFollow = useCallback(
    (subscribedBy: boolean) => {
      updateUserInCache({ ...user, subscribedBy })
    },
    [updateUserInCache, user],
  )
  const handleClickMessage = useCallback(async () => {
    await handleOpenChat(user.id)
    closeCollectionsDialog()
  }, [closeCollectionsDialog, handleOpenChat, user.id])
  const handleOpenNewTab = useCallback(async () => {
    const { id, name, avatar, displayName, username, avatarThumbs } = user

    openNewTab({
      fanId: id,
      // TODO: IMPLEMENT SOLUTION FOR INBOX SPLITTER \ FAN NUMBERING
      // fanNumber,
      avatar: avatarThumbs?.c50 ?? avatarThumbs?.c144 ?? avatar,
      title: displayName || name || username,
    })
  }, [openNewTab, user])
  const { followMutation } = useToggleFollow(onToggleFollow)

  return (
    <div className="no-scrollbar flex max-w-full gap-2 overflow-auto">
      <Tooltip
        content={
          isTabsLimitReached
            ? 'You have reached your maximum tab limit'
            : 'Open Chat in New Tab'
        }
      >
        <Button
          variant="outline"
          className="dark:hover:bg-primary-900 h-[38px] shrink-0 bg-transparent px-2"
          disabled={isTabsLimitReached}
          onClick={handleOpenNewTab}
        >
          <OpenNewTabIcon className="mr-1 shrink-0" />
        </Button>
      </Tooltip>
      {user.canReceiveChatMessage && (
        <Action
          label="Message"
          icon={ChatIcon}
          disabled={isChatOpening}
          onClick={handleClickMessage}
        />
      )}
      {!user.subscribedBy && !user.isBlocked && user.subscribePrice === 0 && (
        <Action
          label="Follow"
          icon={FollowIcon}
          disabled={followMutation.isLoading}
          onClick={() => followMutation.mutate(user.id)}
        />
      )}
      {/* <Action label="Discount" icon={DiscountIcon} onClick={onClickDiscount} /> */}
      <Action
        label={user.isRestricted ? 'Unrestrict' : 'Restrict'}
        icon={RestrictIcon}
        onClick={onClickRestrict}
        disabled={isRestrictActionLoading}
      />
      <Action
        label="Edit Notes"
        icon={NoteIcon}
        onClick={onClickEditNotes}
      />
    </div>
  )
}

interface ActionProps {
  icon: React.ElementType
  label: string
  onClick: () => void
  disabled?: boolean
}

const Action = ({ icon: Icon, label, onClick, disabled }: ActionProps) => {
  return (
    <Button
      variant="outline"
      className="dark:hover:bg-primary-900 h-[38px] shrink-0 bg-transparent px-2"
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className="mr-1 shrink-0" />
      {label}
    </Button>
  )
}
